/**
 * Model picker do chat de TEXTO (header da conversa).
 *
 * Um botão ghost com o label do modelo ativo + chevron, abrindo um dropdown com
 * as opções de `CHAT_MODEL_OPTIONS`. O modelo ativo é `preferences.chatModel`
 * (default `GEMINI_MODELS.chatDefault`); selecionar persiste o campo escalar
 * `chatModel` em `users/{uid}.preferences` via `updateUserPreferences`. O backend
 * relê a preferência a cada turno, então a troca só vale para as próximas
 * mensagens. Segue o padrão de dropdown-picker do `mode-toggle`.
 */
import { CHAT_MODEL_OPTIONS, GEMINI_MODELS } from '@agente/shared'
import { Check, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useUserDoc } from '@/hooks/useUserDoc'
import { useAuth } from '@/lib/auth'
import { updateUserPreferences } from '@/lib/firestore'

export function ModelPicker() {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const { userDoc } = useUserDoc()

  // Preferência ausente OU fora do catálogo atual (ex.: modelo descontinuado
  // como o antigo gemini-2.5-pro) cai no default — mesma allowlist do backend
  // (core.ts#runAgentTurn), sem erro: o picker simplesmente mostra o default
  // selecionado.
  const requestedId = userDoc?.preferences?.chatModel
  const activeId =
    requestedId && CHAT_MODEL_OPTIONS.some((m) => m.id === requestedId)
      ? requestedId
      : GEMINI_MODELS.chatDefault
  const activeLabel =
    CHAT_MODEL_OPTIONS.find((m) => m.id === activeId)?.label ??
    CHAT_MODEL_OPTIONS.find((m) => m.id === GEMINI_MODELS.chatDefault)?.label ??
    CHAT_MODEL_OPTIONS[0].label

  async function selectModel(id: string) {
    if (!uid || id === activeId) return
    try {
      await updateUserPreferences(uid, { chatModel: id })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível trocar o modelo', { description: message })
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Modelo do chat" className="gap-1.5">
          <span className="truncate">{activeLabel}</span>
          <ChevronDown className="size-4 shrink-0 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {CHAT_MODEL_OPTIONS.map(({ id, label }) => (
          <DropdownMenuItem
            key={id}
            onSelect={() => void selectModel(id)}
            className="justify-between"
          >
            {label}
            {id === activeId ? <Check className="size-4" /> : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
          Vale para as próximas mensagens
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
